import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { ReactComponent as ArrowDown } from 'icons/down-arrow.svg';
import { ReactComponent as Pencil } from 'icons/edit-pencil.svg';
import { ReactComponent as TaskReviewedIcon } from 'icons/task-reviewed.svg';
import { ReactComponent as TaskIcon } from 'icons/task.svg';
import { ReactComponent as TrashCan } from 'icons/delete-icon.svg';
import { LEFT_PANELS } from 'constants/feature/panel-constants';
import { smoothScrollIntoTopById } from 'utils/scrolling-utils';
import ToolTip from './tool-tip-component';

export const TREE_VIEW_BLOCK = 'tree-view';
export const TREE_VIEW_ID_BLOCK = 'tree-view-id';
const EDIT_SECTION_MODAL = 'statement-content-panel--edit-section';

const TreeView = ({
  expanded,
  children,
  toggle,
  hidden,
  highlighted,
  id,
  parentId = null,
  label,
  onSelect,
  scrollBasedHighlighted,
  navigationEditMode,
  onOpenEditNavigation,
  onOpenDeleteNavigation,
  sectionReview,
  index,
  panel,
  isNew,
  recentlyAdded,
  setNewSectionId,
  isSideBySideViewMode,
  isOCR,
  showEditAndDeleteIconInRow = false,
  isEditAndDeleteNotPermitted = false,
}) => {
  // this useEffect works as componentDidMount, it is going to scroll to the current treeView if its new and is being previewed
  useEffect(() => {
    if (isNew || recentlyAdded) {
      smoothScrollIntoTopById(`${TREE_VIEW_ID_BLOCK}-${id}`);

      return () => {
        //if there was a recently added section, when this component umount
        // we should reset the newSectionId to avoid unnecessary scrolling
        if (recentlyAdded) {
          setNewSectionId(null);
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ExpandCollapse = ({ expandable }) => {
    return (
      <div
        className={classNames(
          `${TREE_VIEW_BLOCK}__arrow`,
          !expanded && `${TREE_VIEW_BLOCK}__arrow--collapsed`,
        )}
        onClick={(e) => {
          toggle(id);
          e.stopPropagation();
        }}
      >
        {(Array.isArray(expandable) ? expandable.length > 0 : !!expandable) && (
          <ArrowDown />
        )}
      </div>
    );
  };

  const renderIcon = () => {
    return (
      ((panel !== LEFT_PANELS.COMPARE && panel === LEFT_PANELS.STATEMENT_NAV) ||
        isSideBySideViewMode) && (
        <div className={`${TREE_VIEW_BLOCK}__item--col2`}>
          {!isNew &&
          sectionReview &&
          sectionReview.hasSectionBeenReviewed(id, isOCR) ? (
            <TaskReviewedIcon />
          ) : (
            <TaskIcon />
          )}
        </div>
      )
    );
  };

  return (
    <div
      className={`${TREE_VIEW_BLOCK}`}
      id={`${TREE_VIEW_ID_BLOCK}-${id}-${isNew ? 'new' : 'existing'}`}
    >
      <div
        className={classNames(
          `${TREE_VIEW_BLOCK}__item`,
          expanded && `${TREE_VIEW_BLOCK}__item--expanded`,
          highlighted && `${TREE_VIEW_BLOCK}__item--highlighted`,
          scrollBasedHighlighted &&
            `${TREE_VIEW_BLOCK}__item--scroll-highlighted`,
          isNew && `${TREE_VIEW_BLOCK}__item--new-item`,
        )}
        style={{
          display: hidden ? 'none' : null,
        }}
      >
        <div className={`${TREE_VIEW_BLOCK}__item--col1`}>
          <ExpandCollapse expandable={children} />
          {parentId !== null && navigationEditMode && (
            <Pencil
              onClick={onOpenEditNavigation}
              className={`${EDIT_SECTION_MODAL}__pencil-icon`}
            />
          )}
          <span onClick={() => onSelect(id)} title={label}>
            {label}
          </span>
        </div>
        {renderIcon()}
        {showEditAndDeleteIconInRow && (
          <>
            <div
              className={classNames(
                isEditAndDeleteNotPermitted
                  ? `${TREE_VIEW_BLOCK}__item-actions__disabled`
                  : `${TREE_VIEW_BLOCK}__item-actions`,
              )}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <ToolTip
                id={`${TREE_VIEW_ID_BLOCK}-${index}__item-actions-edit-bookmark-tooltip`}
                text={'common.edit'}
                position={'top'}
                usePortal={false}
              >
                <Pencil
                  onClick={(e) => {
                    !isEditAndDeleteNotPermitted && onOpenEditNavigation();
                    e.stopPropagation();
                  }}
                  className={`${EDIT_SECTION_MODAL}__pencil-icon`}
                />
              </ToolTip>
            </div>
            <div
              className={classNames(
                `${TREE_VIEW_BLOCK}__item-actions--delete`,
                isEditAndDeleteNotPermitted || (!isOCR && parentId === null)
                  ? `${TREE_VIEW_BLOCK}__item-actions__disabled`
                  : `${TREE_VIEW_BLOCK}__item-actions`,
              )}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <ToolTip
                id={`${TREE_VIEW_ID_BLOCK}-${index}__item-actions-delete-bookmark-tooltip`}
                text={'common.delete'}
                position={'top'}
                usePortal={false}
              >
                <TrashCan
                  onClick={(e) => {
                    !isEditAndDeleteNotPermitted &&
                      (isOCR || parentId !== null) &&
                      onOpenDeleteNavigation();
                    e.stopPropagation();
                  }}
                  className={`${EDIT_SECTION_MODAL}__pencil-icon`}
                />
              </ToolTip>
            </div>
          </>
        )}
      </div>
      {children && (
        <div
          className={classNames(
            `${TREE_VIEW_BLOCK}__children`,
            !expanded && `${TREE_VIEW_BLOCK}__children--collapsed`,
          )}
        >
          {expanded && children}
        </div>
      )}
    </div>
  );
};

TreeView.propTypes = {
  /** Flag for whether this row is expanded */
  expanded: PropTypes.bool,
  /** Function for expanding/closing this row */
  toggle: PropTypes.func.isRequired,
  /** Flag for if this row should be hidden */
  hidden: PropTypes.bool,
  /** Flag for if this row should be highlighted */
  highlighted: PropTypes.bool,
  /** Unique id corresponding to the data that is represented by this row, returned as the argument in `toggle` prop */
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Label of the row, displayed to the right of the caret */
  label: PropTypes.string.isRequired,
  /** Flag for if this row should be highlighted based on scroll position */
  scrollBasedHighlighted: PropTypes.bool,
  /** Action fired on click of the label */
  onSelect: PropTypes.func.isRequired,
  /** Flag for heading editable function */
  navigationEditMode: PropTypes.bool,
  /** Heading editable function */
  onOpenEditNavigation: PropTypes.func,
  /** Heading delete function */
  onOpenDeleteNavigation: PropTypes.func,
  /** Indicates which left panel is open */
  panel: PropTypes.string,
  /* bool value that indicates if a sections is new */
  isNew: PropTypes.bool,
  /* bool value that indicates if a section has been recently added */
  recentlyAdded: PropTypes.bool,
  /* action to set the new section value */
  setNewSectionId: PropTypes.func,
  /**show or hide extra icons in a row */
  showEditAndDeleteIconInRow: PropTypes.bool,
  /**If it has permission to edit and delete */
  isEditAndDeleteNotPermitted: PropTypes.bool,
};

export default TreeView;
